import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { adminHeaders } from '../utils/adminHeaders';
import {
    Typography,
    Input,
    Button,
    Card,
    CardContent,
    Sheet,
    Divider,
    Grid,
    Box,
    Stack,
    Chip,
    Alert,
} from '@mui/joy';
import {
    Search,
    Person,
    Email,
    Add,
} from '@mui/icons-material';

const API_BASE = process.env.REACT_APP_ADMIN_API_BASE || 'http://localhost:9300';

const ClientsManagement = () => {
    const { session } = useAuth();
    const [emailQuery, setEmailQuery] = useState('');
    const [userResults, setUserResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const navigate = useNavigate();

    const searchUsers = async () => {
        if (!emailQuery.trim()) {
            setMessage('Please enter an email to search');
            return;
        }

        try {
            setLoading(true);
            setMessage('');
            
            const res = await fetch(`${API_BASE}/search/client?email=${encodeURIComponent(emailQuery)}`, {
                headers: adminHeaders(session),
            });
            
            if (!res.ok) {
                throw new Error(`Search failed: ${res.status}`);
            }
            
            const users = await res.json();
            setUserResults(users);
            setMessage(users.length ? '' : 'No clients found');
        } catch (error) {
            console.error('Search error:', error);
            setMessage('Failed to search clients. Please try again.');
            setUserResults([]);
        } finally {
            setLoading(false);
        }
    };

    const handleKeyPress = (event) => {
        if (event.key === 'Enter') {
            searchUsers();
        }
    };

    const clearSearch = () => {
        setEmailQuery('');
        setUserResults([]);
        setMessage('');
    };

    return (
        <Sheet sx={{ p: 4, maxWidth: '1400px', mx: 'auto' }}>
            {/* Header */}
            <Box sx={{ mb: 4 }}>
                <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                    <Person sx={{ fontSize: 32, color: 'success.500' }} />
                    <Box>
                        <Typography level="h2" sx={{ mb: 0.5 }}>
                            Client Management
                        </Typography>
                        <Typography level="body-lg" textColor="neutral.600">
                            Search, view, and manage client accounts
                        </Typography>
                    </Box>
                </Stack>
            </Box>

            {/* Search Section */}
            <Card variant="outlined" sx={{ mb: 4 }}>
                <CardContent>
                    <Typography level="h4" sx={{ mb: 3 }}>
                        Search Clients
                    </Typography>
                    
                    <Grid container spacing={2} alignItems="end">
                        <Grid xs={12} md={8}>
                            <Typography level="body-sm" sx={{ mb: 1 }}>
                                Email Address
                            </Typography>
                            <Input
                                placeholder="Enter client email address"
                                value={emailQuery}
                                onChange={(e) => setEmailQuery(e.target.value)}
                                onKeyPress={handleKeyPress}
                                startDecorator={<Email />}
                                size="lg"
                                disabled={loading}
                            />
                        </Grid>
                        <Grid xs={12} md={4}>
                            <Stack direction="row" spacing={2}>
                                <Button 
                                    onClick={searchUsers} 
                                    variant="solid"
                                    color="success"
                                    startDecorator={<Search />}
                                    loading={loading}
                                    disabled={!emailQuery.trim()}
                                    size="lg"
                                    sx={{ flex: 1 }}
                                >
                                    Search
                                </Button>
                                {(emailQuery || userResults.length > 0) && (
                                    <Button 
                                        onClick={clearSearch}
                                        variant="outlined"
                                        color="neutral"
                                        size="lg"
                                    >
                                        Clear
                                    </Button>
                                )}
                            </Stack>
                        </Grid>
                    </Grid>


                </CardContent>
            </Card>

            {/* Messages */}
            {message && (
                <Alert 
                    color={userResults.length > 0 ? "success" : "warning"} 
                    sx={{ mb: 3 }}
                >
                    {message}
                </Alert>
            )}

            {/* Results Section */}
            {userResults.length > 0 && (
                <Box>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                        <Typography level="h4">
                            Search Results
                        </Typography>
                        <Chip color="success" variant="soft">
                            {userResults.length} client{userResults.length === 1 ? '' : 's'} found
                        </Chip>
                    </Stack>
                    
                    <Grid container spacing={3}>
                        {userResults.map((user) => (
                            <Grid key={user.id} xs={12} md={6} lg={4}>
                                <Card 
                                    onClick={() => navigate(`/client/${user.id}`)} 
                                    variant="outlined" 
                                    sx={{ 
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease',
                                        '&:hover': {
                                            transform: 'translateY(-2px)',
                                            boxShadow: 'md',
                                        },
                                    }}
                                >
                                    <CardContent>
                                        <Stack spacing={2}>
                                            <Stack direction="row" alignItems="center" spacing={2}>
                                                <Box
                                                    sx={{
                                                        p: 1.5,
                                                        borderRadius: '50%',
                                                        backgroundColor: 'success.softBg',
                                                        color: 'success.500',
                                                    }}
                                                >
                                                    <Person sx={{ fontSize: 20 }} />
                                                </Box>
                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                    <Typography level="title-md" sx={{ mb: 0.5 }}>
                                                        {user.name}
                                                    </Typography>
                                                    <Typography 
                                                        level="body-sm" 
                                                        textColor="neutral.500"
                                                        sx={{ 
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap',
                                                        }}
                                                    >
                                                        {user.email}
                                                    </Typography>
                                                </Box>
                                            </Stack>
                                            
                                            <Divider />
                                            
                                            <Grid container spacing={1}>
                                                <Grid xs={6}>
                                                    <Typography level="body-xs" textColor="neutral.600">
                                                        Log Level
                                                    </Typography>
                                                    <Typography level="body-sm" fontWeight="bold">
                                                        {user.log_level}
                                                    </Typography>
                                                </Grid>
                                                <Grid xs={6}>
                                                    <Typography level="body-xs" textColor="neutral.600">
                                                        Client ID
                                                    </Typography>
                                                    <Typography level="body-sm" fontWeight="bold">
                                                        {user.id}
                                                    </Typography>
                                                </Grid>
                                            </Grid>
                                        </Stack>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </Box>
            )}

            {/* Empty State */}
            {!loading && userResults.length === 0 && !message && (
                <Card variant="soft" sx={{ p: 4, textAlign: 'center' }}>
                    <Person sx={{ fontSize: 48, color: 'neutral.400', mb: 2 }} />
                    <Typography level="h4" sx={{ mb: 1 }}>
                        Search for Clients
                    </Typography>
                    <Typography level="body-sm" textColor="neutral.500">
                        Enter a client email address above to start searching
                    </Typography>
                </Card>
            )}
        </Sheet>
    );
};

export default ClientsManagement; 